<template>
  <span id="settlements-contract-actions">
    <Icon
    v-for="(action, index) of actionIcons"
    :key="index"
    :icon="action.icon"
    :iconColor="action.color"
    :tooltipText="$t(action.tooltip)"
    :disabled="action.disabled"
    @icon-clicked="action.click()"
    :elementId="action.elementId"
    />
    <Dialog :stateId="dialogId"
    :enforcePersistence="editContractDialog && companyInfo.requireApprovalForContractModifications"
    @dialog-closing="close">
      <ContractDetail
      v-if="contractDetailDialog"
      :contractId="contract.contractId"
      @edit-contract="editContract"
      @edit-activities="editContract"
      />
      <EditContract
      v-if="editContractDialog"
      :contractId="contract.contractId"
      shouldAddActivity
      @contract-changed="refreshAndClose('edited')"
      @contract-changed-keep-open="$emit('edited')"
      @close="close"
      :companyRequiresEditApproval="companyInfo.requireApprovalForContractModifications"
      :contractMode="contractMode"
      />
    </Dialog>
    <v-dialog v-model="unpostTicketsDialog" max-width="400px">
      <ConfirmDialog
        v-if="unpostTicketsDialog"
        :title="$t('unpostTickets')"
        :confirmText="$t('unpostTickets')"
        :body="unpostTicketsBody"
        @cancel="close"
        @confirm="unpostTicketsFromContract"
        >
        <template #progress-bar>
          <v-row justify="center">
            <v-progress-circular
            indeterminate
            v-if="unpostingLoadingIndicator"
            color="primary"
            >
            </v-progress-circular>
          </v-row>
        </template>
      </ConfirmDialog>
    </v-dialog>
  </span>
</template>

<script>
import { uniqueDialogId } from '@/utils/componentHelpers'
import { UserClaims, ContractMode, ContractType } from '../../utils/Enumerations'
import { userAssignedClaim } from '../../utils/ClaimUtility'
import { mapActions, mapGetters } from 'vuex'
import { groupBy } from 'lodash'
export default {
  name: 'SettlementsContractActions',

  components: {
    Dialog: () => import('@/components/Dialog.vue'),
    ContractDetail: () => import('@/components/contract/contract-detail/ContractDetail.vue'),
    EditContract: () => import('@/components/contract/ContractEditor.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    ConfirmDialog: () => import('@/components/helper/ConfirmDialog.vue')
  },

  props: {
    contract: {
      type: Object,
      required: true
    },
    tickets: {
      type: Array,
      required: false,
      default: () => []
    },
    hideEditAndUnpostActions: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    dialogId: uniqueDialogId('contract-card-actions'),
    contractDetailDialog: false,
    editContractDialog: false,
    unpostTicketsDialog: false,
    unpostingLoadingIndicator: false
  }),

  computed: {
    ...mapGetters('user', ['companyInfo']),
    ...mapGetters('settlements', ['dateConfiguration']),
    actionIcons () {
      const icons = [
        {
          icon: 'mdi-altimeter',
          tooltip: 'viewContract',
          color: 'primary',
          elementId: 'contract',
          click: this.viewContractDetail
        }
      ]
      if (!this.hideEditAndUnpostActions) {
        icons.push(
          {
            icon: 'mdi-pencil',
            tooltip: 'edit',
            color: 'success',
            elementId: 'edit-contract',
            click: this.editContract,
            disabled: !userAssignedClaim(UserClaims.ContractManager)
          },
          {
            icon: 'mdi-briefcase',
            tooltip: 'unpostTickets',
            color: 'error',
            elementId: 'unpost',
            click: this.showUnpostDialog
          }
        )
      }
      return icons
    },

    contractMode () {
      switch (this.contract.type) {
        case ContractType.Production.value:
        case ContractType.WoodsSale.value:
          return ContractMode.Logs
        case ContractType.LogYardSale.value:
          return ContractMode.LogYardSale
        case ContractType.ByproductSale.value:
        case ContractType.ByproductPurchase.value:
        case ContractType.ByproductHauling.value:
          return ContractMode.Byproducts
        case ContractType.Transfer.value:
          return ContractMode.Transfers
        default:
          return ContractMode.Logs
      }
    },

    unpostTicketsBody () {
      return this.tickets.length === 1 ? this.$t('unpostOneTicket') : this.$t('unpostXTickets', { x: this.tickets.length })
    }
  },

  methods: {
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    ...mapActions('posting', ['unpostTickets']),
    viewContractDetail () {
      this.close()
      this.contractDetailDialog = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '80%' })
    },

    editContract () {
      this.close()
      this.editContractDialog = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '80%' })
    },

    showUnpostDialog () {
      if (this.dateConfiguration?.exportBatch?.exportBatchId !== undefined) {
        this.setSnackError(this.$t('ticketsCannotBeUnpostedInExportBatch'))
        return
      }
      this.close()
      this.unpostTicketsDialog = true
    },

    async unpostTicketsFromContract () {
      const groupedTickets = groupBy(this.tickets, 'postBatchId')
      this.unpostingLoadingIndicator = true

      for (const postBatchId in groupedTickets) {
        const ticketIds = groupedTickets[postBatchId].map(ticket => ticket.ticketId)
        await this.unpostTickets({ postBatchId, ticketIds })
      }

      this.unpostingLoadingIndicator = false

      this.setSnack(this.$t('ticketsUnposted', { ticketCount: this.tickets.length }))
      this.refreshAndClose('unposted')
    },

    close () {
      this.closeSubdialogs()
      this.closeDialogsAtOrAbove(this.dialogId)
    },

    closeSubdialogs () {
      this.contractDetailDialog = false
      this.editContractDialog = false
      this.unpostTicketsDialog = false
    },

    refreshAndClose (emission) {
      this.$emit(emission)
      this.close()
    }
  }
}
</script>
